import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { SmartToy } from '@mui/icons-material';
import { AbeMessage as AbeMessageType } from '../../store/zustand/abe';
import { mainColors } from '../../theme/themeConfig';
import AbeChart from './AbeChart';

interface AbeMessageProps {
  message: AbeMessageType;
  onConfirm?: (payload: any) => void;
  onDismiss?: () => void;
  thinkingStatus?: string;
}

function AbeMessageBubble({
  message,
  thinkingStatus,
  onConfirm,
  onDismiss
}: AbeMessageProps): JSX.Element {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Box
          sx={{
            bgcolor: mainColors.MAIN,
            borderRadius: '18px 18px 4px 18px',
            color: '#fff',
            maxWidth: '75%',
            px: 2,
            py: 1.25
          }}
        >
          <Typography
            sx={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}
            variant="body2"
          >
            {message.content}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 1, mb: 1.5 }}>
      {/* Abe avatar */}
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: mainColors.MAIN,
          borderRadius: '50%',
          display: 'flex',
          flexShrink: 0,
          height: 28,
          justifyContent: 'center',
          mt: 0.25,
          width: 28
        }}
      >
        <SmartToy sx={{ color: '#fff', fontSize: 16 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Message bubble */}
        <Box
          sx={{
            bgcolor: '#f5f7fa',
            borderRadius: '4px 18px 18px 18px',
            maxWidth: '90%',
            px: 2,
            py: 1.25
          }}
        >
          {message.isStreaming && !message.content ? (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                py: 0.75
              }}
            >
              {/* Bouncing dots */}
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      '@keyframes abeBounce': {
                        '0%, 80%, 100%': {
                          opacity: 0.3,
                          transform: 'scale(0.6)'
                        },
                        '40%': { opacity: 1, transform: 'scale(1)' }
                      },
                      animation: 'abeBounce 1.2s infinite',
                      animationDelay: `${i * 0.18}s`,
                      bgcolor: mainColors.MAIN,
                      borderRadius: '50%',
                      height: 7,
                      width: 7
                    }}
                  />
                ))}
              </Box>
              {/* Thinking status */}
              {thinkingStatus && (
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                  <Box
                    sx={{
                      '@keyframes abeSpin': {
                        '100%': { transform: 'rotate(360deg)' }
                      },
                      animation: 'abeSpin 1s linear infinite',
                      border: `2px solid ${mainColors.MAIN_1}`,
                      borderRadius: '50%',
                      borderTopColor: mainColors.MAIN,
                      flexShrink: 0,
                      height: 10,
                      width: 10
                    }}
                  />
                  <Typography
                    sx={{
                      '@keyframes abeStatusFade': {
                        from: { opacity: 0, transform: 'translateY(2px)' },
                        to: { opacity: 1, transform: 'translateY(0)' }
                      },
                      animation: 'abeStatusFade 0.3s ease-in',
                      color: mainColors.MAIN,
                      fontSize: '0.7rem',
                      fontStyle: 'italic'
                    }}
                  >
                    {thinkingStatus}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography
              sx={{
                color: 'text.primary',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap'
              }}
              variant="body2"
            >
              {message.content}
              {message.isStreaming && (
                <Box
                  component="span"
                  sx={{
                    '@keyframes abeCursor': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0 }
                    },
                    animation: 'abeCursor 1s step-end infinite',
                    bgcolor: mainColors.MAIN,
                    display: 'inline-block',
                    height: 14,
                    ml: 0.25,
                    verticalAlign: 'middle',
                    width: 2
                  }}
                />
              )}
            </Typography>
          )}
        </Box>

        {/* Inline chart — shown below bubble once streaming completes */}
        {message.chartData && !message.isStreaming && (
          <Box sx={{ maxWidth: '90%', mt: 1 }}>
            <AbeChart
              compact
              data={message.chartData}
              title={message.chartData?.title}
            />
          </Box>
        )}

        {/* Confirmation */}
        {message.requiresConfirmation &&
          message.confirmationPayload &&
          !message.isStreaming && (
            <Box
              sx={{
                bgcolor: 'warning.50',
                border: '1px solid',
                borderColor: 'warning.light',
                borderRadius: 2,
                maxWidth: '90%',
                mt: 1,
                p: 1.5
              }}
            >
              <Typography
                sx={{ color: 'warning.dark', display: 'block', mb: 1 }}
                variant="caption"
              >
                {message.confirmationPayload.message}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={() => onConfirm?.(message.confirmationPayload)}
                  size="small"
                  sx={{ bgcolor: mainColors.MAIN, fontSize: '0.75rem' }}
                  variant="contained"
                >
                  Confirm
                </Button>
                <Button
                  onClick={onDismiss}
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                  variant="outlined"
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}
      </Box>
    </Box>
  );
}

AbeMessageBubble.defaultProps = {
  onConfirm: undefined,
  onDismiss: undefined,
  thinkingStatus: ''
};

export default AbeMessageBubble;
